import {
  Box,
  makeStyles,
  Table,
  TableContainer,
  colors,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import React, { ReactNode } from "react";
import ExpandMoreOutlined from "@material-ui/icons/ExpandMore";
import { FormattedMessage, useIntl } from "react-intl";

interface TableDataProps {
  paginator?: TablePaginatorProps;
}

const useStyles = makeStyles(
  (theme) => ({
    root: {
      width: "100%",
    },

    paginator: {
      paddingRight: 10,
    },

    table: {
      width: "100%",

      "& thead tr th": {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
      },

      "& tbody tr td": {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
      },
    },
  }),
  { name: "table-data-styles" }
);

export default function TableData(props: TableDataProps) {
  const classes = useStyles(props);
  // Props
  const { paginator, ...rest } = props;

  return (
    <Box className={classes.root} {...rest}>
      <Box className={classes.paginator}>
        {paginator ? <TablePaginator {...paginator} /> : <></>}
      </Box>
      <TableContainer component="div">
        <Table
          stickyHeader
          className={classes.table}
          aria-label="app sticky table"
        ></Table>
      </TableContainer>
    </Box>
  );
}

const usePaginatorStyles = makeStyles(
  (theme) => ({
    root: {
      paddingBottom: theme.spacing(2),
    },

    wrapper: {
      width: "100%",
      paddingLeft: theme.spacing(4),
      paddingRight: 4,
    },

    actionsWrapper: {
      flexShrink: 0,
    },

    actions: {
      color: colors.grey[800],
    },

    actionsPrev: {
      transform: "rotate(90deg)",
      marginLeft: "auto",
      marginRight: 4,
      color: theme.palette.text.primary,
    },

    actionsNext: {
      transform: "rotate(-90deg)",
      marginLeft: "auto",
      color: theme.palette.text.primary,
    },

    titles: {
      fontSize: 13,
      color: colors.grey[800],
      marginRight: 20,
      width: "100%",
    },
  }),
  { name: "table-paginator-styles" }
);

interface TablePaginatorProps {
  page: number;
  totalPage: number;
  total: number;
  title: ReactNode;
  loading?: boolean;
  handlePrevAction?: () => void | null;
  handleNextAction?: () => void | null;
}

export function TablePaginator(props: TablePaginatorProps) {
  const classes = usePaginatorStyles(props);
  const intl = useIntl();
  // Props
  const {
    page,
    title,
    total,
    totalPage,
    handleNextAction,
    handlePrevAction,
    loading,
    ...rest
  } = props;

  return (
    <Box className={classes.root} {...rest}>
      <Box
        component="div"
        className={classes.wrapper}
        display="flex"
        alignItems="start"
      >
        <Box className={classes.titles}>{title}</Box>

        <Box
          className={classes.actionsWrapper}
          display="flex"
          alignItems="center"
          mt={1}
        >
          {loading ? (
            <Box display="inline-block" mr={4}>
              <CircularProgress color="secondary" size={16} thickness={4} />
            </Box>
          ) : (
            <></>
          )}
          <Box component="div">
            <Box component="div" className={classes.titles}>
              <FormattedMessage
                id="sharedScreen.paginatorDynamicPage"
                values={{ page: page, totalPage: totalPage }}
              />
            </Box>
          </Box>
          <Box className={classes.actions}>
            <IconButton
              className={classes.actionsPrev}
              onClick={handlePrevAction}
              disabled={page === 1}
            >
              <ExpandMoreOutlined
                fontSize="small"
                color={page === 1 ? "disabled" : "inherit"}
                titleAccess={intl.formatMessage({
                  id: "sharedScreen.previous",
                })}
              />
            </IconButton>
            <IconButton
              onClick={handleNextAction}
              className={classes.actionsNext}
              disabled={page === totalPage}
            >
              <ExpandMoreOutlined
                fontSize="small"
                color={page === totalPage ? "disabled" : "inherit"}
                titleAccess={intl.formatMessage({ id: "sharedScreen.next" })}
              />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

TablePaginator.defaultProps = {
  loading: false,
};
